import React, { useEffect, useState } from "react";
import { Routes as ReactRoutes, Route, useLocation } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { LoginCallback } from "@okta/okta-react";

import ViewQuote from "./modules/viewQuote/viewQuote";
import Display from "./modules/display/display";
import CustomizeQuote from "./modules/customizeQuote/customizeQuote";
import CustomQuotePDF from "./modules/customizeQuote/customQuotePDF";
import CreateQuote from "./modules/createQuote/createQuote";
import GroupQuoteView from "./modules/groupQuote/view/groupQuoteView";
import ReviewGroupQuotes from "./modules/reviewGroupQuotes/reviewGroupQuotes";
import ViewQuoteItinerary from "./modules/viewQuoteItinerary/viewQuoteItinerary";

import SignIn from "./modules/okta/signin";

import { getProfile } from "modules/common/store/profileSlice";
import { getBusinessLogo } from "modules/common/store/businessLogoSlice";
import getApplicationMessages from "services/contentful/contentful.service";
import getGroupTypes from "services/groupType/groupType.service";

import PrivateRoute from "utilities/privateRoute";
import GroupContract from "components/groupContract/groupContract";
import ViewItineraryDeviation from "modules/viewItineraryDeviation/vewItineraryDeviation";
import Container from "components/groupContract/Container";
import GenerateQuote from "components/common/generateQuote/generateQuote";
import TechIssueAlert from "components/common/popups/techIssueAlert/techIssueAlert";
import SignInAlert from "components/common/popups/signInAlert/signInAlert";
import InVoice from "components/common/inVoice/inVoice";
import ViewInvoice from "modules/viewInvoice/viewInvoice";
import { connect } from "react-redux";
import commonUtilities from "utilities/common";
import getSfdc from "services/sfdc/sfdc.service";
import LoadingSpinner from "components/common/loadingSpinner/loadingSpinner";
import { CONFIGURATION } from "utilities/configuration";

const Routes = (props) => {

    const { authState, oktaAuth } = useOktaAuth();
    const location = useLocation();
    const [profileLoaded, setProfileLoaded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showTechIssueAlert, setShowTechIssueAlert] = useState(false);
    const [showSignInAlert, setShowSignInAlert] = useState(false);

    const authDetect = async () => {
        
        if(isDisplayPath(location)) return;
        
        let hasSession = false;
        const leadNumber = getLeadNumber(location);
        
        try
        {
            // Check if the user has an Okta session
            hasSession = await oktaAuth.session.exists();
            
            if(hasSession) {
                // get token
                let response = await oktaAuth.token.getWithoutPrompt();
                //console.log(response);
                //set tokens
                oktaAuth.tokenManager.setTokens(response.tokens);
                if (!oktaAuth.isLoginRedirect()) {
                    // Trigger an initial authState change event when the app startup
                    await oktaAuth.authStateManager.updateAuthState();

                    if(!profileLoaded) {
                        setShowSpinner(true);
                        const response = await props.dispatch(getProfile());
                        if(response?.payload?.data && response.payload.data.errorCode === undefined && (response.payload.data.statusCode === 200 || response.payload.data.statusCode === undefined)) {
                            if(commonUtilities.isIQMUser()) {
                                if (leadNumber) {
                                    await props.dispatch(getSfdc(leadNumber));
                                }
                            }

                            await props.dispatch(getApplicationMessages());

                            await props.dispatch(getGroupTypes());

                            setProfileLoaded(true);
                            if(commonUtilities.isAgent()) {
                                await props.dispatch(getBusinessLogo());
                            }
                        }
                        else {
                            setShowTechIssueAlert(true);
                        }
                        setShowSpinner(false);
                    }
                }
            } else {
                // No session — prompt login
                if(!leadNumber && CONFIGURATION.SIGN_IN_ENABLE) {
                    oktaAuth.setOriginalUri(window.location.href); // or a specific route like '/dashboard'
                    oktaAuth.signInWithRedirect();
                }
            }
        }
        catch(err) {
            console.error("Silent login failed", err);
            if(hasSession && CONFIGURATION.SIGN_IN_ENABLE) {
                setShowSignInAlert(true);
            }
        }
    };

    const getLeadNumber = (location) => {
        const segments = location.pathname.split("/"); // e.g., ['', 'lead', '123']
        if(segments?.length > 2) {
            if(segments[1].toLowerCase() === "lead") {
                return segments[2];
            }
        }
        return null;
    };

    const isDisplayPath = (location) => {
        const segments = location.pathname.split("/"); // e.g., ['', 'display', 'url=xxx']
        if(segments?.length > 2) {
            if(segments[1].toLowerCase() === "display") {
                return true;
            }
        }
        return false;
    };

    const onCancel = () => {
        setShowTechIssueAlert(false);
    };

    useEffect(() => {
        authDetect();
    }, [location]);

    
    useEffect(() => {
    }, [showTechIssueAlert]);

    const { inProcessing, inLoading } = props;

    return (
        <div id="qma-container">
            <ReactRoutes>
                <Route exact path="/" element={<SignIn />} />
                <Route exact path="/home" element={<PrivateRoute profileLoaded={profileLoaded}><CreateQuote/></PrivateRoute>} />
                <Route path="/review" element={<PrivateRoute profileLoaded={profileLoaded}><ReviewGroupQuotes/></PrivateRoute>} />
                <Route path="/quote/:quoteNumber" element={<PrivateRoute profileLoaded={profileLoaded}><ViewQuote/></PrivateRoute>} />
                <Route path="/:type/quote/:quoteNumber" element={<PrivateRoute profileLoaded={profileLoaded}><ViewQuote/></PrivateRoute>} />
                <Route path="/quote/:quoteNumber/customize" element={<PrivateRoute profileLoaded={profileLoaded}><CustomizeQuote/></PrivateRoute>} />
                <Route path="/:type/quote/:quoteNumber/customize" element={<PrivateRoute profileLoaded={profileLoaded}><CustomizeQuote/></PrivateRoute>} />
                <Route path="/quote/:quoteNumber/customize/pdf" element={<PrivateRoute profileLoaded={profileLoaded}><CustomQuotePDF/></PrivateRoute>} />
                <Route path="/:type/quote/:quoteNumber/customize/pdf" element={<PrivateRoute profileLoaded={profileLoaded}><CustomQuotePDF/></PrivateRoute>} />
                <Route path="/lead/:leadNumber/groupContract" element={<PrivateRoute profileLoaded={profileLoaded}><Container/></PrivateRoute>} />
                <Route path="/lead/:leadNumber/generateQuote/:quoteNumber" element={<PrivateRoute profileLoaded={profileLoaded}><GenerateQuote/></PrivateRoute>} />
                <Route exact path="/lead/:leadNumber/inVoice" element={<PrivateRoute profileLoaded={profileLoaded}><InVoice/></PrivateRoute>} />
                <Route exact path="/lead/:leadNumber/ViewInvoice" element={<PrivateRoute profileLoaded={profileLoaded}><ViewInvoice/></PrivateRoute>} />
                <Route exact path="/lead/:leadNumber/quote" element={<PrivateRoute profileLoaded={profileLoaded}><CreateQuote/></PrivateRoute>} />
                <Route path="/lead/:leadNumber/quote/:quoteNumber" element={<PrivateRoute profileLoaded={profileLoaded}><GroupQuoteView/></PrivateRoute>} />
                <Route path="/lead/:leadNumber/quote/:quoteNumber/itinerary/:itineraryID" element={<PrivateRoute profileLoaded={profileLoaded}><ViewQuoteItinerary/></PrivateRoute>} />
                <Route path="/lead/:leadNumber/quote/:quoteNumber/itinerary/deviation/:itineraryID" element={<PrivateRoute profileLoaded={profileLoaded}><ViewItineraryDeviation/></PrivateRoute>} />                            
                <Route path="/display/:languageCode/:url" element={<Display/>} />
                <Route path="/group" element={GroupContract} />                           
                <Route path="/login/callback" element={<LoginCallback />} />
            </ReactRoutes>
            
            {inProcessing ? <div className="root-loader">
                <object className="loader-image" id="svgLoader" data={CONFIGURATION.SPIN_LOADER_URL} type="image/svg+xml"></object>
            </div> : null}
            {showTechIssueAlert ? <TechIssueAlert isShow={showTechIssueAlert} onCancel={onCancel}/> : null}
            {showSignInAlert ? <SignInAlert isShow={showSignInAlert} onCancel={onCancel}/> : null}
            {inLoading || showSpinner ? <LoadingSpinner/> : null }
        </div>
    );
};

const mapStateToProps = (state) => ({
    inProcessing: state.inProcessing,
    inLoading: state.inLoading
});

export default connect(mapStateToProps)(Routes);
